import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';
import { detect } from "solo-web-engine";
import { handleDetectionResult } from "../utils/bioFeedback";
import { buildExtractionPlan, formatFFmpegTime } from "../utils/videoFaceDetectorBatches";

const postDebugLog = ({ hypothesisId, location, message, data = {} }) => {
    console.info('[VA_DEBUG]', {
        hypothesisId,
        location,
        message,
        data,
        timestamp: Date.now(),
    });
};

const getAverage = (values) => {
    if (!values || values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const roundMetric = (value, digits = 3) => (
    Number.isFinite(value) ? Number(value.toFixed(digits)) : null
);

/**
 * VideoFaceDetector
 *
 * Processes a video file frame-by-frame using FFmpeg.wasm for deterministic
 * frame extraction that matches the VA Pro backend pipeline exactly.
 *
 * Pipeline:
 *   1. Load FFmpeg.wasm (needs SharedArrayBuffer / cross-origin isolation)
 *   2. Write the video File directly to FFmpeg's virtual FS (avoids blob-URL fetch)
 *   3. Extract all frames as lossless PNGs using the same filter chain as the backend:
 *      ffmpeg -i input -vf "fps={N}" frame_%04d.png
 *   4. For each frame: run MediaPipe in IMAGE mode, save result, delete PNG.
 *   5. Call onFinishDetection() when done.
 *
 * Progress stages via onProgress({ stage, frameIndex, totalFrames, currentTime, duration }):
 *   'loading' → 'extracting' → 'analyzing'
 */

class VideoFaceDetector extends Component {

    constructor(props) {
        super(props);
        this._isMounted = false;
        this._aborted = false;
        this._ffmpeg = null;
        this._ffmpegLogs = [];
        this._extractionProgressTimer = null;
        this._videoStartedNotified = false;
        this._progressRuntime = this._createProgressRuntime();
    }

    componentDidMount() {
        this._isMounted = true;
        // #region agent log
        postDebugLog({
            hypothesisId: 'H5',
            location: 'src/components/VideoFaceDetector.js:componentDidMount',
            message: 'VideoFaceDetector mounted',
            data: {
                hasVideoFile: !!this.props.videoFile,
                srcKind: typeof this.props.src,
                interval: this.props.interval,
            },
        });
        // #endregion
        this._run();
    }

    componentWillUnmount() {
        this._isMounted = false;
        this._aborted = true;
        this._stopExtractionProgressTicker();
        // #region agent log
        postDebugLog({
            hypothesisId: 'H5',
            location: 'src/components/VideoFaceDetector.js:componentWillUnmount',
            message: 'VideoFaceDetector unmounted',
            data: {
                bufferedFfmpegLogs: this._ffmpegLogs.length,
            },
        });
        // #endregion
    }

    _createProgressRuntime = () => ({
        mode: 'single',
        stage: 'loading',
        totalFrames: 0,
        duration: 0,
        batchIndex: 0,
        batchCount: 1,
        batchStartFrame: 0,
        batchFrameIndex: 0,
        batchFrameCount: 0,
        batchStartTime: 0,
        batchDuration: 0,
        ffmpegSpeed: null,
        extractionStartedAtMs: null,
        extractionBatchDurationsSec: [],
        extractionSecPerMediaSecSamples: [],
        analysisPerFrameSamplesSec: [],
        overallProgressPct: 0,
    });

    _resetProgressRuntime = () => {
        this._progressRuntime = this._createProgressRuntime();
    }

    _buildProgressPayload = (overrides = {}) => {
        const runtime = this._progressRuntime;
        return {
            mode: runtime.mode,
            stage: runtime.stage,
            frameIndex: 0,
            totalFrames: runtime.totalFrames,
            currentTime: 0,
            duration: runtime.duration,
            batchIndex: runtime.batchIndex,
            batchCount: runtime.batchCount,
            batchStartFrame: runtime.batchStartFrame,
            batchFrameIndex: runtime.batchFrameIndex,
            batchFrameCount: runtime.batchFrameCount,
            batchStartTime: runtime.batchStartTime,
            batchDuration: runtime.batchDuration,
            batchEtaSec: null,
            overallEtaSec: null,
            ffmpegSpeed: runtime.ffmpegSpeed,
            overallProgressPct: runtime.overallProgressPct,
            ...overrides,
        };
    }

    _emitProgress = (overrides = {}) => {
        const { onProgress } = this.props;
        onProgress && onProgress(this._buildProgressPayload(overrides));
    }

    _logMeasurement = (label, data = {}) => {
        console.info('[VA_MEASURE]', {
            label,
            ...data,
            timestamp: Date.now(),
        });
    }

    _getEstimatedCurrentBatchExtractionSec = () => {
        const runtime = this._progressRuntime;
        if (runtime.batchDuration <= 0) {
            return null;
        }

        const avgSecPerMediaSec = getAverage(runtime.extractionSecPerMediaSecSamples);
        if (avgSecPerMediaSec && avgSecPerMediaSec > 0) {
            return runtime.batchDuration * avgSecPerMediaSec;
        }

        // Conservative fallback until we have historical samples.
        return runtime.batchDuration;
    }

    _getEstimatedFutureExtractionSec = () => {
        const runtime = this._progressRuntime;
        const remainingBatchCount = Math.max(runtime.batchCount - runtime.batchIndex - 1, 0);
        if (remainingBatchCount === 0) return 0;

        const avgSecPerMediaSec = getAverage(runtime.extractionSecPerMediaSecSamples);
        if (!avgSecPerMediaSec) return null;

        const remainingMediaDurationSec = Math.max(
            runtime.duration - (runtime.batchStartTime + runtime.batchDuration),
            0
        );

        return remainingMediaDurationSec * avgSecPerMediaSec;
    }

    _getEstimatedFutureAnalysisSec = (futureFrameCount, fallbackPerFrameSec = null) => {
        if (futureFrameCount <= 0) return 0;
        const averagePerFrameSec = getAverage(this._progressRuntime.analysisPerFrameSamplesSec) || fallbackPerFrameSec;
        if (!averagePerFrameSec) return null;
        return averagePerFrameSec * futureFrameCount;
    }

    _emitExtractionProgress = (mediaTimeSec, speed) => {
        const runtime = this._progressRuntime;
        if (runtime.stage !== 'extracting' || runtime.batchDuration <= 0) {
            return;
        }

        const extractionElapsedSec = runtime.extractionStartedAtMs
            ? (Date.now() - runtime.extractionStartedAtMs) / 1000
            : 0;
        const batchProgressRatio = Math.min(mediaTimeSec / runtime.batchDuration, 1);
        const currentTime = Math.min(runtime.batchStartTime + mediaTimeSec, runtime.duration);
        const batchEtaSec = speed > 0
            ? Math.max(runtime.batchDuration - mediaTimeSec, 0) / speed
            : (batchProgressRatio > 0
                ? Math.max((extractionElapsedSec / batchProgressRatio) - extractionElapsedSec, 0)
                : null);

        const futureExtractionSec = this._getEstimatedFutureExtractionSec();
        const futureAnalysisSec = this._getEstimatedFutureAnalysisSec(
            Math.max(runtime.totalFrames - runtime.batchStartFrame, 0)
        );

        runtime.ffmpegSpeed = speed || null;
        runtime.overallProgressPct = runtime.duration > 0
            ? Math.round((currentTime / runtime.duration) * 100)
            : 0;

        this._emitProgress({
            stage: 'extracting',
            frameIndex: runtime.batchStartFrame,
            totalFrames: runtime.totalFrames,
            currentTime,
            duration: runtime.duration,
            batchFrameIndex: Math.min(Math.round(batchProgressRatio * runtime.batchFrameCount), runtime.batchFrameCount),
            batchEtaSec,
            overallEtaSec: batchEtaSec != null && futureExtractionSec != null && futureAnalysisSec != null
                ? batchEtaSec + futureExtractionSec + futureAnalysisSec
                : null,
            ffmpegSpeed: speed || null,
            overallProgressPct: runtime.overallProgressPct,
        });
    }

    _emitWallClockExtractionProgress = () => {
        const runtime = this._progressRuntime;
        if (runtime.stage !== 'extracting' || !runtime.extractionStartedAtMs) {
            return;
        }

        const elapsedSec = (Date.now() - runtime.extractionStartedAtMs) / 1000;
        const estimatedTotalSec = this._getEstimatedCurrentBatchExtractionSec();
        if (!estimatedTotalSec || estimatedTotalSec <= 0) {
            return;
        }

        const progressRatio = Math.min(elapsedSec / estimatedTotalSec, 0.99);
        const mediaTimeSec = runtime.batchDuration * progressRatio;
        const speed = elapsedSec > 0 ? mediaTimeSec / elapsedSec : null;
        this._emitExtractionProgress(mediaTimeSec, speed);
    }

    _startExtractionProgressTicker = () => {
        this._stopExtractionProgressTicker();
        this._extractionProgressTimer = window.setInterval(() => {
            this._emitWallClockExtractionProgress();
        }, 1000);
    }

    _stopExtractionProgressTicker = () => {
        if (this._extractionProgressTimer) {
            window.clearInterval(this._extractionProgressTimer);
            this._extractionProgressTimer = null;
        }
    }

    _loadFFmpeg = async () => {
        this._ffmpegLogs = [];
        const ffmpeg = createFFmpeg({
            corePath: `${window.location.origin}/ffmpeg-core.js`,
            log: false,
            logger: () => {},
        });
        await ffmpeg.load();
        this._ffmpeg = ffmpeg;
        return ffmpeg;
    }

    _getVideoDuration = (videoFile, src) => (
        new Promise((resolve, reject) => {
            const video = document.createElement('video');
            let objectUrl = null;

            const cleanup = () => {
                video.onloadedmetadata = null;
                video.onerror = null;
                video.removeAttribute('src');
                video.load();
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };

            video.preload = 'metadata';
            video.muted = true;
            video.playsInline = true;

            video.onloadedmetadata = () => {
                const duration = video.duration;
                cleanup();
                if (Number.isFinite(duration) && duration > 0) {
                    resolve(duration);
                    return;
                }
                reject(new Error('Unable to determine video duration'));
            };

            video.onerror = () => {
                cleanup();
                reject(new Error('Failed to load video metadata'));
            };

            if (videoFile) {
                objectUrl = URL.createObjectURL(videoFile);
                video.src = objectUrl;
            } else {
                video.src = src;
            }
        })
    );

    _notifyVideoStarted = (videoStarted) => {
        if (!this._videoStartedNotified && videoStarted) {
            this._videoStartedNotified = true;
            videoStarted();
        }
    }

    _getFrameFiles = (ffmpeg, frameRegex) => (
        ffmpeg.FS('readdir', '/')
            .filter((filename) => frameRegex.test(filename))
            .sort()
    );

    _cleanupFiles = (ffmpeg, filenames) => {
        filenames.forEach((filename) => {
            try {
                ffmpeg.FS('unlink', filename);
            } catch (_) {}
        });
    }

    _extractBatch = async ({ ffmpeg, inputName, sarFilter, batch, plannedDurationSec, onProgress }) => {
        const args = [];
        const runtime = this._progressRuntime;

        runtime.stage = 'extracting';
        runtime.batchIndex = batch.index;
        runtime.batchStartFrame = batch.startFrame;
        runtime.batchFrameIndex = 0;
        runtime.batchFrameCount = batch.frameCount;
        runtime.batchStartTime = batch.startSec;
        runtime.batchDuration = batch.durationSec;
        runtime.ffmpegSpeed = null;
        runtime.extractionStartedAtMs = Date.now();
        runtime.overallProgressPct = plannedDurationSec > 0
            ? Math.round((batch.startSec / plannedDurationSec) * 100)
            : 0;
        this._startExtractionProgressTicker();

        this._emitProgress({
            stage: 'extracting',
            frameIndex: batch.startFrame,
            totalFrames: runtime.totalFrames,
            currentTime: batch.startSec,
            duration: plannedDurationSec,
            batchFrameIndex: 0,
            batchEtaSec: null,
            overallEtaSec: null,
            ffmpegSpeed: null,
            overallProgressPct: runtime.overallProgressPct,
        });

        // #region agent log
        postDebugLog({
            hypothesisId: 'H3',
            location: 'src/components/VideoFaceDetector.js:_extractBatch:start',
            message: 'Starting FFmpeg batch extraction',
            data: {
                batchIndex: batch.index,
                startFrame: batch.startFrame,
                frameCount: batch.frameCount,
                startSec: batch.startSec,
                durationSec: batch.durationSec,
            },
        });
        // #endregion

        if (batch.startSec > 0) {
            args.push('-ss', formatFFmpegTime(batch.startSec));
        }
        args.push(
            '-i', inputName,
            '-map', '0:v:0',
            '-an',
            '-sn',
            '-dn'
        );
        if (batch.durationSec > 0) {
            args.push('-t', formatFFmpegTime(batch.durationSec));
        }
        args.push(
            '-vf', sarFilter,
            '-frames:v', String(batch.frameCount),
            '-start_number', '0',
            batch.outputPattern
        );

        try {
            await ffmpeg.run(...args);
        } finally {
            this._stopExtractionProgressTicker();
        }

        const extractionElapsedSec = runtime.extractionStartedAtMs
            ? (Date.now() - runtime.extractionStartedAtMs) / 1000
            : null;
        if (extractionElapsedSec != null) {
            runtime.extractionBatchDurationsSec.push(extractionElapsedSec);
            if (batch.durationSec > 0) {
                runtime.extractionSecPerMediaSecSamples.push(extractionElapsedSec / batch.durationSec);
            }
        }
        runtime.ffmpegSpeed = extractionElapsedSec > 0 && batch.durationSec > 0
            ? batch.durationSec / extractionElapsedSec
            : null;
        runtime.extractionStartedAtMs = null;

        const extractedFiles = this._getFrameFiles(ffmpeg, batch.frameRegex);
        const frameFiles = extractedFiles.slice(0, batch.frameCount);
        const overflowFiles = extractedFiles.slice(batch.frameCount);

        if (overflowFiles.length > 0) {
            this._cleanupFiles(ffmpeg, overflowFiles);
        }

        // #region agent log
        postDebugLog({
            hypothesisId: 'H3',
            location: 'src/components/VideoFaceDetector.js:_extractBatch:complete',
            message: 'Completed FFmpeg batch extraction',
            data: {
                batchIndex: batch.index,
                extractedFrames: frameFiles.length,
                expectedFrames: batch.frameCount,
            },
        });
        // #endregion

        this._logMeasurement('batch_extraction', {
            batchIndex: batch.index,
            extractedFrames: frameFiles.length,
            expectedFrames: batch.frameCount,
            mediaDurationSec: roundMetric(batch.durationSec),
            elapsedSec: roundMetric(extractionElapsedSec),
            framesPerSec: roundMetric(extractionElapsedSec > 0 ? frameFiles.length / extractionElapsedSec : null),
            realtimeSpeed: roundMetric(extractionElapsedSec > 0 ? batch.durationSec / extractionElapsedSec : null),
            ffmpegSpeed: roundMetric(runtime.ffmpegSpeed),
        });

        return {
            frameFiles,
            extractionElapsedSec,
        };
    }

    _analyzeFrameFiles = async ({
        ffmpeg,
        frameFiles,
        batchIndex,
        startFrameIndex,
        totalFrames,
        fps,
        duration,
        videoStarted,
        options,
        useMaskModel,
    }) => {
        const runtime = this._progressRuntime;

        runtime.stage = 'analyzing';
        runtime.batchIndex = batchIndex;
        runtime.batchStartFrame = startFrameIndex;
        runtime.batchFrameIndex = 0;
        runtime.batchFrameCount = frameFiles.length;
        runtime.batchStartTime = startFrameIndex / fps;
        runtime.batchDuration = frameFiles.length / fps;

        if (frameFiles.length > 0) {
            this._notifyVideoStarted(videoStarted);
        }

        const batchAnalysisStartedAtMs = Date.now();

        for (let i = 0; i < frameFiles.length; i++) {
            if (!this._isMounted || this._aborted) break;

            const filename = frameFiles[i];
            const globalFrameIndex = startFrameIndex + i;
            const currentTime = +(globalFrameIndex / fps).toFixed(3);
            const frameStartedAtMs = Date.now();

            try {
                const pngData = ffmpeg.FS('readFile', filename);
                ffmpeg.FS('unlink', filename);

                if (globalFrameIndex === 0) {
                    // #region agent log
                    postDebugLog({
                        hypothesisId: 'H2',
                        location: 'src/components/VideoFaceDetector.js:_analyzeFrameFiles:firstFrame',
                        message: 'First extracted frame entered analysis',
                        data: {
                            filename,
                            pngBytes: pngData.length,
                            totalFrames,
                        },
                    });
                    // #endregion
                }

                const blob = new Blob([pngData.buffer], { type: 'image/png' });
                let frameSource;

                if (typeof createImageBitmap === 'function') {
                    frameSource = await createImageBitmap(blob, {
                        colorSpaceConversion: 'none',
                        premultiplyAlpha: 'none',
                    });
                } else {
                    const imageURL = URL.createObjectURL(blob);
                    frameSource = new Image();
                    frameSource.src = imageURL;
                    await new Promise((resolve, reject) => {
                        frameSource.onload = resolve;
                        frameSource.onerror = reject;
                    });
                    URL.revokeObjectURL(imageURL);
                }

                const { detectionsWithExpressions, faceMeshResult } = await detect(frameSource, {
                    emotionsModelActive: true,
                    handsModelActive: true,
                    maskModelActive: useMaskModel,
                    showFaceLandmarks: false,
                    emotionsModelOptions: options,
                    returnOriginalFacemesh: true,
                });

                const trackerItem = handleDetectionResult(detectionsWithExpressions, currentTime);

                this.props.saveResults({
                    emotions: trackerItem.emotions,
                    currentTime,
                    result: trackerItem.result,
                    faceMeshResult,
                });
            } catch (frameErr) {
                console.warn(`[VideoFaceDetector] Frame ${globalFrameIndex} skipped:`, frameErr);
            } finally {
                const frameElapsedSec = (Date.now() - frameStartedAtMs) / 1000;
                const analyzedCount = i + 1;
                const batchElapsedSec = (Date.now() - batchAnalysisStartedAtMs) / 1000;
                const avgBatchFrameSec = analyzedCount > 0 ? (batchElapsedSec / analyzedCount) : null;
                const batchFramesRemaining = Math.max(frameFiles.length - analyzedCount, 0);
                const futureBatchesRemaining = Math.max(runtime.batchCount - batchIndex - 1, 0);
                const futureFramesAfterBatch = Math.max(totalFrames - (startFrameIndex + frameFiles.length), 0);
                const futureExtractionSec = this._getEstimatedFutureExtractionSec();
                const futureAnalysisSec = this._getEstimatedFutureAnalysisSec(futureFramesAfterBatch, avgBatchFrameSec);
                const batchEtaSec = avgBatchFrameSec != null ? avgBatchFrameSec * batchFramesRemaining : null;

                runtime.analysisPerFrameSamplesSec.push(frameElapsedSec);
                runtime.batchFrameIndex = analyzedCount;
                runtime.overallProgressPct = totalFrames > 0
                    ? Math.round((analyzedCount + startFrameIndex) / totalFrames * 100)
                    : 0;

                this._emitProgress({
                    stage: 'analyzing',
                    frameIndex: globalFrameIndex,
                    totalFrames,
                    currentTime,
                    duration,
                    batchFrameIndex: analyzedCount,
                    batchEtaSec,
                    overallEtaSec: batchEtaSec != null && futureExtractionSec != null && futureAnalysisSec != null
                        ? batchEtaSec + futureExtractionSec + futureAnalysisSec
                        : (batchEtaSec != null && futureBatchesRemaining === 0 && futureAnalysisSec != null
                            ? batchEtaSec + futureAnalysisSec
                            : null),
                    ffmpegSpeed: null,
                    overallProgressPct: runtime.overallProgressPct,
                });
            }
        }

        const analysisElapsedSec = (Date.now() - batchAnalysisStartedAtMs) / 1000;
        const analyzedFrames = Math.min(runtime.batchFrameIndex, frameFiles.length);
        this._logMeasurement('batch_analysis', {
            batchIndex,
            analyzedFrames,
            batchFrameCount: frameFiles.length,
            elapsedSec: roundMetric(analysisElapsedSec),
            framesPerSec: roundMetric(analysisElapsedSec > 0 ? analyzedFrames / analysisElapsedSec : null),
            avgFrameMs: roundMetric(analyzedFrames > 0 ? (analysisElapsedSec * 1000) / analyzedFrames : null),
        });

        return {
            analysisElapsedSec,
            analyzedFrames,
        };
    }

    _runSinglePassExtraction = async ({
        ffmpeg,
        inputName,
        sarFilter,
        fps,
        plannedDurationSec,
        onProgress,
        videoStarted,
        options,
        useMaskModel,
    }) => {
        console.log('[VideoFaceDetector] Running FFmpeg at', fps, 'fps with SAR correction…');
        const extractionStartedAtMs = Date.now();
        const runtime = this._progressRuntime;
        runtime.stage = 'extracting';
        runtime.batchIndex = 0;
        runtime.batchStartFrame = 0;
        runtime.batchFrameIndex = 0;
        runtime.batchFrameCount = runtime.totalFrames;
        runtime.batchStartTime = 0;
        runtime.batchDuration = plannedDurationSec;
        runtime.ffmpegSpeed = null;
        runtime.extractionStartedAtMs = extractionStartedAtMs;
        this._startExtractionProgressTicker();
        // #region agent log
        postDebugLog({
            hypothesisId: 'H1',
            location: 'src/components/VideoFaceDetector.js:_runSinglePassExtraction:start',
            message: 'Invoking ffmpeg.run for single-pass extraction',
            data: {
                inputName,
                fps,
                sarFilter,
            },
        });
        // #endregion

        try {
            await ffmpeg.run(
                '-i', inputName,
                '-map', '0:v:0',
                '-an',
                '-sn',
                '-dn',
                '-vf', sarFilter,
                '-start_number', '0',
                'frame_%04d.png'
            );
        } finally {
            this._stopExtractionProgressTicker();
        }
        const extractionElapsedSec = (Date.now() - extractionStartedAtMs) / 1000;
        if (plannedDurationSec > 0) {
            runtime.extractionSecPerMediaSecSamples.push(extractionElapsedSec / plannedDurationSec);
        }
        runtime.ffmpegSpeed = extractionElapsedSec > 0 && plannedDurationSec > 0
            ? plannedDurationSec / extractionElapsedSec
            : null;
        runtime.extractionStartedAtMs = null;

        if (!this._isMounted || this._aborted) return;

        const allFiles = ffmpeg.FS('readdir', '/');
        const frameFiles = allFiles
            .filter((filename) => /^frame_\d{4}\.png$/.test(filename))
            .sort();

        const totalFrames = frameFiles.length;
        const duration = totalFrames > 0 ? (totalFrames - 1) / fps : plannedDurationSec;

        console.log('[VideoFaceDetector] PNG frames extracted:', totalFrames, '| All FS files:', allFiles.join(', '));
        // #region agent log
        postDebugLog({
            hypothesisId: 'H3',
            location: 'src/components/VideoFaceDetector.js:_runSinglePassExtraction:complete',
            message: 'Single-pass ffmpeg.run completed',
            data: {
                totalFrames,
                fsEntryCount: allFiles.length,
                fsSample: frameFiles.slice(0, 3),
                ffmpegLogTail: this._ffmpegLogs.slice(-5),
            },
        });
        // #endregion

        if (totalFrames === 0) {
            console.warn('[VideoFaceDetector] FFmpeg produced 0 frames. Last FFmpeg logs:');
            this._ffmpegLogs.slice(-20).forEach((logLine) => console.warn('  ', logLine));
            return;
        }

        const analysisResult = await this._analyzeFrameFiles({
            ffmpeg,
            frameFiles,
            batchIndex: 0,
            startFrameIndex: 0,
            totalFrames,
            fps,
            duration,
            videoStarted,
            options,
            useMaskModel,
        });

        this._logMeasurement('single_pass_extraction', {
            extractedFrames: totalFrames,
            mediaDurationSec: roundMetric(duration),
            elapsedSec: roundMetric(extractionElapsedSec),
            framesPerSec: roundMetric(extractionElapsedSec > 0 ? totalFrames / extractionElapsedSec : null),
            realtimeSpeed: roundMetric(extractionElapsedSec > 0 ? duration / extractionElapsedSec : null),
        });

        return {
            extractedFrames: totalFrames,
            analyzedFrames: analysisResult?.analyzedFrames || totalFrames,
            extractionElapsedSec,
            analysisElapsedSec: analysisResult?.analysisElapsedSec || 0,
        };
    }

    _runBatchedExtraction = async ({
        ffmpeg,
        inputName,
        sarFilter,
        extractionPlan,
        fps,
        onProgress,
        videoStarted,
        options,
        useMaskModel,
    }) => {
        const { batches, totalFrames, plannedDurationSec } = extractionPlan;
        const totals = {
            extractedFrames: 0,
            analyzedFrames: 0,
            extractionElapsedSec: 0,
            analysisElapsedSec: 0,
        };

        // #region agent log
        postDebugLog({
            hypothesisId: 'H3',
            location: 'src/components/VideoFaceDetector.js:_runBatchedExtraction:plan',
            message: 'Using batched extraction plan',
            data: {
                batchCount: batches.length,
                totalFrames,
                plannedDurationSec,
            },
        });
        // #endregion

        for (const batch of batches) {
            if (!this._isMounted || this._aborted) break;

            const { frameFiles, extractionElapsedSec } = await this._extractBatch({
                ffmpeg,
                inputName,
                sarFilter,
                batch,
                plannedDurationSec,
                onProgress,
            });

            if (frameFiles.length === 0) {
                throw new Error(`Batch ${batch.index} produced 0 frames`);
            }

            totals.extractedFrames += frameFiles.length;
            totals.extractionElapsedSec += extractionElapsedSec || 0;

            if (frameFiles.length !== batch.frameCount) {
                console.warn(
                    `[VideoFaceDetector] Batch ${batch.index} extracted ${frameFiles.length}/${batch.frameCount} frames`
                );
            }

            const { analysisElapsedSec, analyzedFrames } = await this._analyzeFrameFiles({
                ffmpeg,
                frameFiles,
                batchIndex: batch.index,
                startFrameIndex: batch.startFrame,
                totalFrames,
                fps,
                duration: plannedDurationSec,
                videoStarted,
                options,
                useMaskModel,
            });

            totals.analyzedFrames += analyzedFrames || 0;
            totals.analysisElapsedSec += analysisElapsedSec || 0;
        }

        return totals;
    }

    _run = async () => {
        // Destructure stable props only. onFinishDetection and saveResults are accessed via
        // this.props at call time so they always reflect the latest render (and latest tracker).
        const { src, videoFile, interval, onProgress, videoStarted, options, useMaskModel } = this.props;
        const fps = Math.round(1000 / interval); // 250ms → 4fps
        let ffmpeg = null;
        let inputName = null;
        const runStartedAtMs = Date.now();
        let sourceLoadStartedAtMs = null;
        let sourceLoadElapsedSec = null;
        let fsWriteStartedAtMs = null;
        let fsWriteElapsedSec = null;
        let executionTotals = {
            extractedFrames: 0,
            analyzedFrames: 0,
            extractionElapsedSec: 0,
            analysisElapsedSec: 0,
        };

        try {
            this._resetProgressRuntime();
            // #region agent log
            postDebugLog({
                hypothesisId: 'H1',
                location: 'src/components/VideoFaceDetector.js:_run:start',
                message: 'Background extraction started',
                data: {
                    fps,
                    fileName: videoFile?.name || null,
                    fileSize: videoFile?.size || null,
                    fileType: videoFile?.type || null,
                    usesBlobSrc: !videoFile && typeof src === 'string',
                },
            });
            // #endregion
            // Stage: loading
            this._emitProgress({ stage: 'loading', frameIndex: 0, totalFrames: 0, currentTime: 0, duration: 0 });

            ffmpeg = await this._loadFFmpeg();
            if (!this._isMounted || this._aborted) return;

            console.log('[VideoFaceDetector] FFmpeg loaded. crossOriginIsolated =', window.crossOriginIsolated);

            let durationSec = 0;
            try {
                durationSec = await this._getVideoDuration(videoFile, src);
                // #region agent log
                postDebugLog({
                    hypothesisId: 'H3',
                    location: 'src/components/VideoFaceDetector.js:_run:duration',
                    message: 'Loaded video metadata duration',
                    data: {
                        durationSec,
                    },
                });
                // #endregion
            } catch (durationErr) {
                console.warn('[VideoFaceDetector] Unable to determine video duration:', durationErr);
            }

            const extractionPlan = buildExtractionPlan(durationSec, fps);
            const plannedDurationSec = extractionPlan.plannedDurationSec || durationSec;
            this._progressRuntime.mode = extractionPlan.shouldBatch ? 'batched' : 'single';
            this._progressRuntime.totalFrames = extractionPlan.totalFrames;
            this._progressRuntime.duration = plannedDurationSec;
            this._progressRuntime.batchCount = extractionPlan.shouldBatch ? extractionPlan.batches.length : 1;

            this._emitProgress({
                stage: 'extracting',
                frameIndex: 0,
                totalFrames: extractionPlan.totalFrames,
                currentTime: 0,
                duration: plannedDurationSec,
            });

            // Prefer the raw File object (passed as videoFile prop) for reliability.
            // fetchFile(File) uses FileReader directly, bypassing any blob-URL/COEP issues.
            // Fall back to src (blob URL) if videoFile is not available.
            sourceLoadStartedAtMs = Date.now();
            let sourceData = videoFile
                ? await fetchFile(videoFile)
                : await fetchFile(src);
            sourceLoadElapsedSec = (Date.now() - sourceLoadStartedAtMs) / 1000;

            console.log('[VideoFaceDetector] Video data loaded:', sourceData.length, 'bytes');
            // #region agent log
            postDebugLog({
                hypothesisId: 'H3',
                location: 'src/components/VideoFaceDetector.js:_run:sourceData',
                message: 'Video bytes loaded into memory',
                data: {
                    sourceBytes: sourceData.length,
                },
            });
            // #endregion
            if (!this._isMounted || this._aborted) return;

            // Detect input extension for the virtual FS filename
            inputName = videoFile
                ? ('input.' + (videoFile.name.split('.').pop() || 'mp4'))
                : 'input.mp4';

            fsWriteStartedAtMs = Date.now();
            ffmpeg.FS('writeFile', inputName, sourceData);
            fsWriteElapsedSec = (Date.now() - fsWriteStartedAtMs) / 1000;
            sourceData = null;
            const stat = ffmpeg.FS('stat', inputName);
            console.log('[VideoFaceDetector] Written to FS:', inputName, stat.size, 'bytes');

            if (stat.size === 0) {
                console.error('[VideoFaceDetector] Input file is 0 bytes in FS — aborting');
                if (this._isMounted) this.props.onFinishDetection && this.props.onFinishDetection();
                return;
            }

            // Run FFmpeg with the exact same filter chain as the backend pipeline:
            //   setpts=PTS-STARTPTS  — normalize start timestamps
            //   fps=fps=N:round=near — deterministic frame selection
            //   scale=SAR correction — critical: corrects non-square pixel SAR so faces
            //                         have the correct aspect ratio for face-api detection.
            //                         Without this, videos with SAR≠1:1 produce squished
            //                         frames and completely wrong expression scores.
            //   setsar=1             — mark output pixels as square
            //   PNG output           — lossless, matches pipeline exactly
            const sarFilter = [
                'setpts=PTS-STARTPTS',
                `fps=fps=${fps}:round=near`,
                "scale='if(gt(sar,1),trunc(iw*sar/2)*2,iw)':'if(gt(sar,0)*lt(sar,1),trunc(ih/sar/2)*2,ih)':flags=lanczos",
                'setsar=1',
            ].join(',');
            if (extractionPlan.shouldBatch) {
                executionTotals = await this._runBatchedExtraction({
                    ffmpeg,
                    inputName,
                    sarFilter,
                    extractionPlan,
                    fps,
                    onProgress,
                    videoStarted,
                    options,
                    useMaskModel,
                });
            } else {
                executionTotals = await this._runSinglePassExtraction({
                    ffmpeg,
                    inputName,
                    sarFilter,
                    fps,
                    plannedDurationSec,
                    onProgress,
                    videoStarted,
                    options,
                    useMaskModel,
                });
            }

            this._logMeasurement('run_summary', {
                mode: extractionPlan.shouldBatch ? 'batched' : 'single',
                batchCount: extractionPlan.shouldBatch ? extractionPlan.batches.length : 1,
                totalFrames: extractionPlan.totalFrames,
                plannedDurationSec: roundMetric(plannedDurationSec),
                sourceLoadSec: roundMetric(sourceLoadElapsedSec),
                fsWriteSec: roundMetric(fsWriteElapsedSec),
                extractionSec: roundMetric(executionTotals.extractionElapsedSec),
                analysisSec: roundMetric(executionTotals.analysisElapsedSec),
                totalRunSec: roundMetric((Date.now() - runStartedAtMs) / 1000),
                extractedFrames: executionTotals.extractedFrames,
                analyzedFrames: executionTotals.analyzedFrames,
                extractionFramesPerSec: roundMetric(
                    executionTotals.extractionElapsedSec > 0
                        ? executionTotals.extractedFrames / executionTotals.extractionElapsedSec
                        : null
                ),
                analysisFramesPerSec: roundMetric(
                    executionTotals.analysisElapsedSec > 0
                        ? executionTotals.analyzedFrames / executionTotals.analysisElapsedSec
                        : null
                ),
            });

            if (this._isMounted && !this._aborted) {
                this.props.onFinishDetection && this.props.onFinishDetection();
            }

        } catch (err) {
            console.error('[VideoFaceDetector] Fatal error:', err);
            console.warn('[VideoFaceDetector] Last FFmpeg logs:', this._ffmpegLogs.slice(-10));
            // #region agent log
            postDebugLog({
                hypothesisId: 'H4',
                location: 'src/components/VideoFaceDetector.js:_run:catch',
                message: 'VideoFaceDetector fatal error',
                data: {
                    errorName: err?.name || null,
                    errorMessage: err?.message || String(err),
                    ffmpegLogTail: this._ffmpegLogs.slice(-10),
                },
            });
            // #endregion
            if (this._isMounted) {
                this.props.onFinishDetection && this.props.onFinishDetection();
            }
        } finally {
            if (ffmpeg && inputName) {
                try { ffmpeg.FS('unlink', inputName); } catch (_) {}
            }
        }
    }

    render() {
        return null;
    }
}

VideoFaceDetector.propTypes = {
    interval: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    videoFile: PropTypes.instanceOf(File),
    onFinishDetection: PropTypes.func,
    options: PropTypes.any,
    onProgress: PropTypes.func,
    saveResults: PropTypes.func.isRequired,
    videoStarted: PropTypes.func,
    useMaskModel: PropTypes.bool,
};

VideoFaceDetector.defaultProps = {
    useMaskModel: false,
    videoFile: null,
};

export default VideoFaceDetector;
